import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plus, Trash2, Archive, ArchiveRestore, X } from "lucide-react";
import { api, Intervention, InterventionType } from "../lib/api";
import { useAuth, isAnalystOrHigher } from "../lib/auth";

const TYPES: InterventionType[] = ["equipment", "training", "grant", "mentoring", "complex"];
const TYPE_LABELS: Record<InterventionType, string> = {
  equipment: "Обладнання",
  training: "Навчання",
  grant: "Грант",
  mentoring: "Менторство",
  complex: "Комплексна",
};

export function InterventionsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [filterType, setFilterType] = useState<InterventionType | "">("");
  const [includeArchived, setIncludeArchived] = useState(false);

  const canEdit = isAnalystOrHigher(user?.role);

  const { data, isLoading, error } = useQuery({
    queryKey: ["interventions", filterType, includeArchived],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filterType) params.set("type", filterType);
      if (includeArchived) params.set("include_archived", "true");
      return api.get<Intervention[]>(`/api/v1/interventions?${params.toString()}`);
    },
  });

  const create = useMutation({
    mutationFn: (payload: any) => api.post<Intervention>("/api/v1/interventions", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["interventions"] });
      setAdding(false);
    },
  });

  const archive = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      api.patch<Intervention>(`/api/v1/interventions/${id}`, { is_archived: archived }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["interventions"] }),
  });

  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/interventions/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["interventions"] }),
  });

  return (
    <div style={{ padding: "24px 32px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, color: "#1a365d", margin: 0 }}>
          Інтервенції фонду {data && <span style={{ color: "#718096", fontSize: 14 }}>({data.length})</span>}
        </h1>
        {canEdit && (
          <button onClick={() => setAdding(true)} style={btnPrimary}>
            <Plus size={14} /> Нова інтервенція
          </button>
        )}
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 12, fontSize: 13, alignItems: "center" }}>
        <label>
          Тип:{" "}
          <select value={filterType} onChange={(e) => setFilterType(e.target.value as any)} style={inp}>
            <option value="">всі</option>
            {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
          </select>
        </label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />
          Показати архівні
        </label>
      </div>

      {isLoading && <div>Завантаження…</div>}
      {error && <div style={{ color: "#c53030" }}>Помилка: {(error as any).message}</div>}

      {data && (
        <table style={{ width: "100%", background: "white", borderCollapse: "collapse", fontSize: 13.5, border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
          <thead>
            <tr style={{ background: "#ebf4ff", color: "#2c5282", textAlign: "left" }}>
              <th style={th}>Дата</th>
              <th style={th}>Лікарня</th>
              <th style={th}>Тип</th>
              <th style={th}>Назва</th>
              <th style={th}>Пакети</th>
              <th style={th}>Бюджет (₴)</th>
              <th style={th}>Статус</th>
              {canEdit && <th style={th}>Дії</th>}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && (
              <tr><td colSpan={canEdit ? 8 : 7} style={{ ...td, color: "#718096", textAlign: "center", padding: 24 }}>
                Немає інтервенцій. {canEdit && "Натисніть «Нова інтервенція», щоб додати першу."}
              </td></tr>
            )}
            {data.map((i) => (
              <tr key={i.intervention_id} style={{ borderTop: "1px solid #e2e8f0", opacity: i.is_archived ? 0.55 : 1 }}>
                <td style={td}>{new Date(i.date_start).toLocaleDateString("uk-UA")}</td>
                <td style={td}>
                  <Link to={`/facilities/${i.edrpou}`} style={{ color: "#2c5282", textDecoration: "none" }}>
                    {i.edrpou}
                  </Link>
                </td>
                <td style={td}><span style={typeBadge(i.type)}>{TYPE_LABELS[i.type]}</span></td>
                <td style={{ ...td, maxWidth: 320 }}>{i.title}</td>
                <td style={td}>{i.target_packages?.join(", ") || "—"}</td>
                <td style={{ ...td, textAlign: "right" }}>
                  {i.budget_uah !== null ? Number(i.budget_uah).toLocaleString("uk-UA") : "—"}
                </td>
                <td style={td}>{i.is_archived ? "📁 архів" : "✓ активна"}</td>
                {canEdit && (
                  <td style={td}>
                    <button onClick={() => archive.mutate({ id: i.intervention_id, archived: !i.is_archived })}
                            style={btnSecondary}
                            title={i.is_archived ? "Розархівувати" : "В архів"}>
                      {i.is_archived ? <ArchiveRestore size={12} /> : <Archive size={12} />}
                    </button>
                    <button onClick={() => confirm(`Видалити «${i.title}»?`) && del.mutate(i.intervention_id)}
                            style={{ ...btnSecondary, marginLeft: 4, color: "#e53e3e", borderColor: "#fed7d7" }}
                            title="Видалити">
                      <Trash2 size={12} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {adding && (
        <Modal onClose={() => setAdding(false)}>
          <CreateForm
            onSubmit={(p) => create.mutate(p)}
            error={create.error ? (create.error as any).detail : null}
          />
        </Modal>
      )}
    </div>
  );
}

function CreateForm({ onSubmit, error }: { onSubmit: (p: any) => void; error: string | null }) {
  const [edrpou, setEdrpou] = useState("");
  const [type, setType] = useState<InterventionType>("equipment");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dateStart, setDateStart] = useState(new Date().toISOString().slice(0, 10));
  const [dateEnd, setDateEnd] = useState("");
  const [lag, setLag] = useState<string>("");
  const [packages, setPackages] = useState<string>("");
  const [budget, setBudget] = useState<string>("");

  function submit(e: FormEvent) {
    e.preventDefault();
    const payload: any = {
      edrpou: edrpou.trim(),
      type, title: title.trim(),
      date_start: dateStart,
    };
    if (description.trim()) payload.description = description.trim();
    if (dateEnd) payload.date_end = dateEnd;
    if (lag) payload.expected_lag_months = Number(lag);
    if (packages) {
      const parsed = packages.split(",").map((s) => Number(s.trim())).filter((n) => !Number.isNaN(n));
      if (parsed.length) payload.target_packages = parsed;
    }
    if (budget) payload.budget_uah = Number(budget);
    onSubmit(payload);
  }

  return (
    <form onSubmit={submit}>
      <h3 style={{ marginTop: 0 }}>Нова інтервенція</h3>
      <Field label="ЄДРПОУ лікарні (8-12 цифр)">
        <input value={edrpou} onChange={(e) => setEdrpou(e.target.value)} required style={inp} />
      </Field>
      <Field label="Тип">
        <select value={type} onChange={(e) => setType(e.target.value as InterventionType)} style={inp}>
          {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
        </select>
      </Field>
      <Field label="Назва">
        <input value={title} onChange={(e) => setTitle(e.target.value)} required style={inp} />
      </Field>
      <Field label="Опис (опційно)">
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} style={{ ...inp, minHeight: 60 }} />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <Field label="Дата початку">
          <input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} required style={inp} />
        </Field>
        <Field label="Дата завершення (опційно)">
          <input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} style={inp} />
        </Field>
        <Field label="Очікуваний lag (місяці)">
          <input type="number" min="0" max="120" value={lag} onChange={(e) => setLag(e.target.value)} style={inp} />
        </Field>
        <Field label="Бюджет (₴)">
          <input type="number" min="0" step="0.01" value={budget} onChange={(e) => setBudget(e.target.value)} style={inp} />
        </Field>
      </div>
      <Field label="Цільові пакети (ID через кому)">
        <input value={packages} onChange={(e) => setPackages(e.target.value)} placeholder="3, 5, 8" style={inp} />
      </Field>
      {error && <div style={{ color: "#c53030", fontSize: 13, margin: "8px 0" }}>{error}</div>}
      <button type="submit" style={{ ...btnPrimary, marginTop: 12 }}>Створити</button>
    </form>
  );
}

function Modal({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div onClick={onClose} className="modal-backdrop">
      <div onClick={(e) => e.stopPropagation()} className="modal-card" style={{ width: 540, maxWidth: "92vw", position: "relative" }}>
        <button onClick={onClose} aria-label="close"
                style={{ position: "absolute", top: 12, right: 12, width: 28, height: 28, borderRadius: "50%", background: "transparent", color: "var(--c-text-muted)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "var(--c-surface-hover)"; e.currentTarget.style.color = "var(--c-danger)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--c-text-muted)"; }}>
          <X size={16} />
        </button>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: "block", fontSize: 12, color: "#4a5568", marginBottom: 3 }}>{label}</label>
      {children}
    </div>
  );
}

function typeBadge(type: InterventionType): React.CSSProperties {
  const colors: Record<InterventionType, string> = {
    equipment: "#2c5282", training: "#2f855a", grant: "#805ad5", mentoring: "#dd6b20", complex: "#9b2c2c",
  };
  return {
    display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600,
    color: colors[type], background: colors[type] + "15",
  };
}

const inp: React.CSSProperties = { padding: "7px 10px", fontSize: 13, border: "1px solid #cbd5e0", borderRadius: 4, outline: "none", width: "100%", boxSizing: "border-box" };
const th: React.CSSProperties = { padding: "10px 12px", fontWeight: 600 };
const td: React.CSSProperties = { padding: "8px 12px" };
const btnPrimary: React.CSSProperties = { padding: "7px 14px", background: "#2c5282", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 };
const btnSecondary: React.CSSProperties = { padding: "5px 10px", background: "white", color: "#4a5568", border: "1px solid #cbd5e0", borderRadius: 4, cursor: "pointer", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 };
